// VoiceSynthesis.ts: Creature-intrinsic voice synthesis engine for NeuroPsyMap
// The creature's voice evolves naturally with its neural network, replacing external vowel synthesis
// This is the creature's own voice, not a separate system
//
// DYNAMIC LEARNING-BASED SPEECH: Speech generation adapts to the creature's linguistic development:
// - Infant stage (< 30 nodes): Reduplicated babbling with articulation features
// - Developing stage (30-100 nodes): Phonetic exclamations using mastered elements
// - Advanced stage (100-300 nodes): Prosodic patterns with stress and intonation
// - Mature stage (300+ nodes): Musical utterances with harmonic progressions
//
// Speech is constructed from learned LinguisticElements with full phonetic properties:
// - Vowels: height, backness, roundedness
// - Consonants: place/manner of articulation, voicing
// - Musical: pitch, melodic context, harmonic function
// - Prosodic: stress patterns, tone levels

import { VoiceCharacteristics, LinguisticElement } from './types';
import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { BreathingSystem, VoiceCapacityThresholds } from './breathingSystem';
import { createPhysicalSoundSynthesizer, PhysicalSoundSynthesizer } from './physicalSoundSynthesis';
import { getAudioEngine } from './core/audio';
import { MouthSystem } from './mouthSystem';

// Creature's intrinsic voice system - this IS the creature's voice
export interface CreatureVoiceSystem {
    // Core creature voice synthesis
    speakCreatureThought(thought: string): Promise<void>;
    vocalizeDiscovery(element: LinguisticElement): Promise<void>;
    expressEmotionalState(emotion: string, intensity: number): Promise<void>;
    
    // Adaptive voice evolution
    evolveVoiceWithNetwork(): void;
    adaptVoiceToEnvironment(): void;
    
    // Creature's natural vocalizations
    makeDiscoverySound(element: LinguisticElement): void;
    expressGrowthSound(): void;
    vocalizeConnectionSound(nodeId: number): void;
    
    // Internal voice state
    getCurrentVoiceState(): CreatureVoiceState;
    updateVoiceFromNetworkActivity(): void;
    
    // Stop all creature vocalizations
    silenceCreature(): void;
    
    // Test method for debugging
    testVoice(): void;
}

export interface CreatureVoiceState {
    currentEmotion: string;
    energyLevel: number;
    growthPhase: 'infant' | 'child' | 'adolescent' | 'adult' | 'elder';
    vocalMaturity: number; // 0-1
    discoveryExcitement: number; // 0-1
    connectionHarmony: number; // 0-1
    lastVocalization: number;
    voiceEvolution: number; // 0-1
}

export interface VoiceModulationParams {
    pitchShift: number; // -12 to +12 semitones
    formantShift: number; // -50% to +50%
    vibratoRate: number; // Hz
    vibratoDepth: number; // 0-1
    tremoloRate: number; // Hz
    tremoloDepth: number; // 0-1
    reverbAmount: number; // 0-1
    echoDelay: number; // ms
    echoFeedback: number; // 0-1
}

export class CreatureVoiceSystem implements CreatureVoiceSystem {
    private network: NeuralNetworkSimulator;
    private breathingSystem: BreathingSystem;
    private audioContext: AudioContext | null = null;
    private currentUtterance: SpeechSynthesisUtterance | null = null;
    private voiceCharacteristics: VoiceCharacteristics;
    private voiceState: CreatureVoiceState;
    private isInitialized = false;
    
    // Audit logging
    private voiceAuditEntries: any[] = [];
    private voiceSystemStartTime: number = Date.now();
    
    // Audio processing nodes for creature's natural voice
    private creatureGainNode: GainNode | null = null;
    private creatureFilterNode: BiquadFilterNode | null = null;
    private creatureDelayNode: DelayNode | null = null;
    private creatureReverbNode: ConvolverNode | null = null;
    
    // PHYSICAL SOUND SYNTHESIS
    private physicalSynthesizer: PhysicalSoundSynthesizer | null = null;
    private get mouth(): MouthSystem | null { return (this.network as any).mouthSystem || null; }
    
    // Creature's voice evolution tracking
    private voiceEvolutionStep: number = 0;
    private lastNetworkState: any = null;
    private vocalizationCooldown: number = 0;

    constructor(network: NeuralNetworkSimulator, breathingSystem: BreathingSystem) {
        this.network = network;
        this.breathingSystem = breathingSystem;
        this.voiceCharacteristics = this.initializeCreatureVoice();
        this.voiceState = this.initializeCreatureVoiceState();
        this.initializeCreatureAudio();
        
        this.logVoiceAudit('VOICE_SYSTEM', 'INITIALIZATION', 'Voice system initialized - locked until breathing mastery achieved');
    }

    private initializeCreatureVoice(): VoiceCharacteristics {
        // Creature starts with a simple, infant-like voice that evolves
        const networkAge = this.network.nodes.length / 100; // Rough age based on node count
        const energyLevel = this.network.nodes.length > 0 
            ? this.network.nodes.reduce((sum, node) => sum + (node.energy || 0), 0) / this.network.nodes.length
            : 0.5; // Default energy level when no nodes exist
        
        return {
            pitch: 200 + energyLevel * 50, // Higher energy = higher pitch
            pitchVariation: 15 + networkAge * 5,
            formant1: 600 + networkAge * 100,
            formant2: 1000 + networkAge * 150,
            formant3: 2000 + networkAge * 300,
            attackTime: 30 + networkAge * 10,
            decayTime: 80 + networkAge * 20,
            sustainLevel: 0.6 + networkAge * 0.1,
            releaseTime: 150 + networkAge * 50,
            breathiness: 0.3 - networkAge * 0.1, // Less breathy as it matures
            roughness: 0.2 - networkAge * 0.05, // Smoother as it matures
            personality: this.determineCreaturePersonality(),
            emotionalState: 'curious',
            age: this.determineCreatureAge(networkAge),
            gender: 'neutral',
            speechRate: 0.8 + networkAge * 0.2,
            pauseFrequency: 0.2 - networkAge * 0.05,
            pauseDuration: 300 - networkAge * 50,
            emphasisStrength: 0.4 + networkAge * 0.1,
            intonationRange: 80 + networkAge * 20,
            questionRise: 0.5,
            statementFall: 0.3,
            exclamationRise: 0.7
        };
    }

    // Simple phonation using the current mouth articulators and breathing state
    // Enables exploratory voice without text/phoneme planning
    public phonateWithMouth(durationMs: number = 600, pitch?: number, mouthState?: any): void {
        if (!this.audioContext || !this.physicalSynthesizer) return;
        const mouth = this.mouth;
        if (!mouth) return;
        const breathing = this.breathingSystem.getBreathingState();
        if (!breathing.voiceUnlocked) return;
        if (breathing.cyclePhase !== 'exhale' && breathing.cyclePhase !== 'pause_exhale') return;

        // Use provided state or current mouth state
        const stateToUse = mouthState || mouth.getState();
        const tempState = mouth.getState(); // Save current
        
        // Temporarily set mouth to desired state
        if (mouthState) {
            mouth.setState(stateToUse);
        }

        const f = mouth.computeFormants();
        const basePitch = pitch ?? Math.max(100, Math.min(400, this.voiceCharacteristics.pitch));

        const buffer = this.physicalSynthesizer.synthesizeFormantVoice(
            basePitch,
            { f1: f.f1, f2: f.f2, f3: f.f3 },
            durationMs / 1000,
            true
        );

        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.network.getAudioOutput());
        source.start(this.audioContext.currentTime);

        // Restore original state if we changed it
        if (mouthState) {
            mouth.setState(tempState);
        }
    }

    // Check if phonation is currently possible (breathing allows)
    public canPhonate(): boolean {
        const breathing = this.breathingSystem.getBreathingState();
        if (!breathing.voiceUnlocked) return false;
        if (breathing.cyclePhase !== 'exhale' && breathing.cyclePhase !== 'pause_exhale') return false;
        return true;
    }

    // Get network's "response" to a phonation - measures activity/energy changes
    public getNetworkResponseToPhonation(): number {
        // Measure how much the network responded (energy fluctuations, node activity)
        const avgEnergy = this.network.nodes.length > 0
            ? this.network.nodes.reduce((sum, n) => sum + (n.energy || 0), 0) / this.network.nodes.length
            : 0;
        
        // Check if there's been recent activity/excitement
        const voiceState = this.getCurrentVoiceState();
        const response = avgEnergy * 0.5 + voiceState.discoveryExcitement * 0.3 + (this.voiceState.energyLevel || 0) * 0.2;
        
        return Math.max(0, Math.min(1, response));
    }

    private initializeCreatureVoiceState(): CreatureVoiceState {
        return {
            currentEmotion: 'curious',
            energyLevel: 0.5,
            growthPhase: 'infant',
            vocalMaturity: 0.1,
            discoveryExcitement: 0.8,
            connectionHarmony: 0.3,
            lastVocalization: 0,
            voiceEvolution: 0.0
        };
    }

    private determineCreaturePersonality(): 'warm' | 'cold' | 'energetic' | 'calm' | 'mysterious' | 'playful' | 'wise' | 'youthful' {
        const networkComplexity = this.network.nodes.length;
        if (networkComplexity < 20) return 'playful';
        if (networkComplexity < 50) return 'youthful';
        if (networkComplexity < 100) return 'energetic';
        if (networkComplexity < 200) return 'wise';
        return 'mysterious';
    }

    private determineCreatureAge(networkAge: number): 'child' | 'young' | 'adult' | 'elderly' {
        if (networkAge < 0.5) return 'child';
        if (networkAge < 1.5) return 'young';
        if (networkAge < 3.0) return 'adult';
        return 'elderly';
    }

    private initializeCreatureAudio(): void {
        try {
            // Use the network's existing audio context instead of creating a new one
            // This prevents AudioContext conflicts
            this.audioContext = this.network.audioContext;
            
            if (this.audioContext && this.audioContext.state === 'running') {
                // Create creature's natural voice processing chain
                this.creatureGainNode = this.audioContext.createGain();
                // Click-safe baseline gain
                try {
                    const now = this.audioContext.currentTime;
                    this.creatureGainNode.gain.cancelScheduledValues(now);
                    this.creatureGainNode.gain.setValueAtTime(Math.max(0.0001, this.creatureGainNode.gain.value || 0.0001), now);
                    this.creatureGainNode.gain.linearRampToValueAtTime(0.12, now + 0.02);
                } catch {
                    this.creatureGainNode.gain.value = 0.12;
                }
                this.creatureFilterNode = this.audioContext.createBiquadFilter();
                this.creatureDelayNode = this.audioContext.createDelay(1.0);
                this.creatureReverbNode = this.audioContext.createConvolver();
                
                // Create creature's unique reverb signature
                this.createCreatureReverbImpulse();
                
                // Connect creature's voice chain
                this.creatureGainNode.connect(this.creatureFilterNode);
                this.creatureFilterNode.connect(this.creatureDelayNode);
                this.creatureDelayNode.connect(this.creatureReverbNode);
                try {
                    const engine = getAudioEngine();
                    if (engine.context === this.audioContext) {
                        this.creatureReverbNode.connect(engine.getBusGain('voice'));
                    } else {
                        const out = this.network && (this.network as any).getAudioOutput ? this.network.getAudioOutput() : this.audioContext.destination;
                        this.creatureReverbNode.connect(out);
                    }
                } catch {
                    const out = this.network && (this.network as any).getAudioOutput ? this.network.getAudioOutput() : this.audioContext.destination;
                    this.creatureReverbNode.connect(out);
                }
                
                this.isInitialized = true;
                
                // Initialize PHYSICAL SOUND SYNTHESIZER
                this.physicalSynthesizer = createPhysicalSoundSynthesizer(this.audioContext, this.network);
                console.log('[Creature Voice] Audio system initialized with PHYSICAL MODELING');
            } else {
                // Only log once, don't spam on every frame
                // console.log('[Creature Voice] Network audio context not ready, using speech synthesis only');
                this.audioContext = null;
                this.isInitialized = true; // Still allow speech synthesis
            }
        } catch (error) {
            console.warn('[Creature Voice] Audio initialization failed:', error);
            // Fall back to speech synthesis only
            this.audioContext = null;
            this.isInitialized = true; // Still allow speech synthesis
        }
    }

    private createCreatureReverbImpulse(): void {
        if (!this.audioContext || !this.creatureReverbNode) return;
        
        // Create creature's unique reverb signature based on network characteristics
        const networkComplexity = this.network.nodes.length;
        const length = this.audioContext.sampleRate * (1 + networkComplexity / 100); // Longer reverb for more complex creatures
        const impulse = this.audioContext.createBuffer(2, length, this.audioContext.sampleRate);
        
        for (let channel = 0; channel < 2; channel++) {
            const channelData = impulse.getChannelData(channel);
            for (let i = 0; i < length; i++) {
                // Creature's reverb has organic, evolving characteristics
                const decay = Math.pow(1 - i / length, 1.5 + networkComplexity * 0.01);
                const organicVariation = Math.sin(i * 0.01) * 0.1;
                channelData[i] = (Math.random() * 2 - 1 + organicVariation) * decay;
            }
        }
        
        this.creatureReverbNode.buffer = impulse;
    }

    // Core creature voice methods
    async speakCreatureThought(thought: string): Promise<void> {
        const startTime = performance.now();
        this.logVoiceAudit('VOICE_SYSTEM', 'SPEAK_ATTEMPT', `Attempting to speak: "${thought}"`);
        
        console.log(`[Creature Voice] Attempting to speak: "${thought}"`);
        console.log(`[Creature Voice] Speech synthesis supported: ${this.isSupported()}`);
        console.log(`[Creature Voice] Vocalization cooldown: ${this.vocalizationCooldown}`);
        
        if (!this.isSupported()) {
            this.logVoiceAudit('VOICE_SYSTEM', 'SPEAK_BLOCKED', 'Speech synthesis not supported');
            console.warn('[Creature Voice] Speech synthesis not supported');
            return;
        }
        
        if (this.vocalizationCooldown > 0) {
            this.logVoiceAudit('VOICE_SYSTEM', 'SPEAK_BLOCKED', `Still in cooldown: ${this.vocalizationCooldown}ms`);
            console.log(`[Creature Voice] Still in cooldown, skipping speech`);
            return;
        }
        
        // Check breathing system capacity and voice unlock status
        const breathingThresholds = this.breathingSystem.getVoiceCapacityThresholds();
        const breathingState = this.breathingSystem.getBreathingState();
        
        if (!breathingState.voiceUnlocked) {
            this.logVoiceAudit('VOICE_SYSTEM', 'SPEAK_BLOCKED', `Voice locked - mastery: ${(breathingState.breathingMastery * 100).toFixed(1)}%, stage: ${breathingState.breathingDevelopmentStage}`);
            console.log(`[Creature Voice] Voice locked - breathing mastery required. Current mastery: ${(breathingState.breathingMastery * 100).toFixed(1)}%, Stage: ${breathingState.breathingDevelopmentStage}`);
            return;
        }
        
        if (!breathingThresholds.canSpeak) {
            this.logVoiceAudit('VOICE_SYSTEM', 'SPEAK_BLOCKED', `Breathing constraints - O2: ${breathingState.oxygenLevel.toFixed(2)}, CO2: ${breathingState.carbonDioxideLevel.toFixed(2)}, capacity: ${(breathingState.voiceCapacity * 100).toFixed(1)}%`);
            console.log(`[Creature Voice] Voice blocked by breathing system - oxygen: ${breathingState.oxygenLevel.toFixed(2)}, CO2: ${breathingState.carbonDioxideLevel.toFixed(2)}, capacity: ${(breathingState.voiceCapacity * 100).toFixed(1)}%`);
            return;
        }
        
        // Start voice tracking in breathing system
        if (!this.breathingSystem.startVoice()) {
            console.log(`[Creature Voice] Breathing system denied voice start`);
            return;
        }
        
        const now = Date.now();
        this.vocalizationCooldown = 500; // 0.5 second cooldown for more responsive voice
        
        try {
            // Creature's voice evolves with each thought
            this.evolveVoiceWithNetwork();
            
            // Apply breathing system constraints to voice characteristics
            this.applyBreathingConstraints(breathingThresholds);
            
            // Create utterance with creature's current voice
            this.currentUtterance = new SpeechSynthesisUtterance(thought);
            this.applyCreatureVoiceCharacteristics(this.currentUtterance);
            
            await new Promise<void>((resolve, reject) => {
                if (!this.currentUtterance) return;
                
                this.currentUtterance.onend = () => {
                    this.voiceState.lastVocalization = now;
                    this.breathingSystem.stopVoice();
                    resolve();
                };
                this.currentUtterance.onerror = (error) => {
                    this.breathingSystem.stopVoice();
                    reject(error);
                };
                
                window.speechSynthesis.speak(this.currentUtterance);
            });
            
            console.log(`[Creature Voice] Spoke: "${thought}"`);
        } catch (error) {
            console.warn('[Creature Voice] Failed to speak thought:', error);
            this.breathingSystem.stopVoice();
        } finally {
            setTimeout(() => {
                this.vocalizationCooldown = Math.max(0, this.vocalizationCooldown - 500);
            }, 500);
        }
    }

    vocalizeDiscovery(element: LinguisticElement): Promise<void> {
        console.log(`[Creature Voice] Vocalizing discovery of "${element.phoneticValue}" (${element.type})`);
        const excitement = this.voiceState.discoveryExcitement;
        const thought = this.generateDiscoveryThought(element, excitement);
        return this.speakCreatureThought(thought);
    }

    expressEmotionalState(emotion: string, intensity: number): Promise<void> {
        this.voiceState.currentEmotion = emotion;
        const thought = this.generateEmotionalThought(emotion, intensity);
        return this.speakCreatureThought(thought);
    }

    // Creature's voice evolution
    evolveVoiceWithNetwork(): void {
        const currentNetworkState = {
            nodeCount: this.network.nodes.length,
            energyLevel: this.network.nodes.reduce((sum, node) => sum + (node.energy || 0), 0) / this.network.nodes.length,
            complexity: this.network.linguisticComplexity,
            step: this.network.step
        };
        
        // Only evolve if network has changed significantly
        if (this.lastNetworkState && 
            Math.abs(currentNetworkState.nodeCount - this.lastNetworkState.nodeCount) < 5 &&
            Math.abs(currentNetworkState.energyLevel - this.lastNetworkState.energyLevel) < 0.1) {
            return;
        }
        
        this.lastNetworkState = currentNetworkState;
        this.voiceEvolutionStep++;
        
        // Evolve voice characteristics based on network growth
        const evolutionFactor = this.voiceEvolutionStep / 100; // Gradual evolution
        this.voiceCharacteristics = this.calculateEvolvedVoice(currentNetworkState, evolutionFactor);
        this.voiceState.voiceEvolution = Math.min(1, evolutionFactor);
        this.voiceState.vocalMaturity = Math.min(1, this.voiceState.vocalMaturity + 0.01);
        
        // Update creature's reverb signature
        this.createCreatureReverbImpulse();
        
        console.log(`[Creature Voice] Voice evolved (step ${this.voiceEvolutionStep})`);
    }

    adaptVoiceToEnvironment(): void {
        // Adapt voice based on current network environment
        const avgEnergy = this.network.nodes.length > 0 
            ? this.network.nodes.reduce((sum, node) => sum + (node.energy || 0), 0) / this.network.nodes.length
            : 0.5; // Default energy level when no nodes exist
        const connectionCount = this.network.edges.length;
        
        // Adjust voice based on energy and connections
        this.voiceCharacteristics.pitch = 150 + avgEnergy * 100;
        this.voiceCharacteristics.breathiness = Math.max(0.1, 0.4 - connectionCount * 0.001);
        this.voiceCharacteristics.roughness = Math.max(0.05, 0.3 - avgEnergy * 0.1);
        
        // Update emotional state based on network health
        if (avgEnergy > 0.8) {
            this.voiceState.currentEmotion = 'excited';
        } else if (avgEnergy < 0.3) {
            this.voiceState.currentEmotion = 'contemplative';
        } else {
            this.voiceState.currentEmotion = 'curious';
        }
    }

    // Natural creature vocalizations
    makeDiscoverySound(element: LinguisticElement): void {
        console.log(`[Creature Voice] Making PHYSICAL discovery sound for "${element.phoneticValue}" (${element.type})`);
        
        try {
            // Create natural sound for discovery using PHYSICAL MODELING
            if (!this.audioContext || !this.creatureGainNode || this.audioContext.state !== 'running') {
                console.log('[Creature Voice] Audio context not available, using speech synthesis only');
                return;
            }
            
            // Get excitement level
            const excitement = this.voiceState.discoveryExcitement;
            
            // Determine base frequency from element type
            let frequency = 440; // Base frequency
            if (element.type === 'vowel') frequency = 550;
            else if (element.type === 'consonant') frequency = 330;
            else if (element.type === 'syllable') frequency = 660;
            
            let buffer: AudioBuffer;
            
            // Use PHYSICAL MODELING if synthesizer is available
            if (this.physicalSynthesizer) {
                console.log(`[Creature Voice] Using PHYSICAL MODELING for discovery sound`);
                buffer = this.physicalSynthesizer.synthesizeDiscoverySound(frequency, 0.3, excitement);
            } else {
                // Fallback to simple oscillator
                console.warn('[Creature Voice] Physical synthesizer not available, using fallback');
                const oscillator = this.audioContext.createOscillator();
                const gain = this.audioContext.createGain();
                
                oscillator.connect(gain);
                gain.connect(this.creatureGainNode);
                
                oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
                oscillator.type = 'sine';
                
                // Quick, excited sound
                gain.gain.setValueAtTime(0, this.audioContext.currentTime);
                gain.gain.linearRampToValueAtTime(0.001, this.audioContext.currentTime + 0.1); // Reduced to prevent clipping
                gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
                
                oscillator.start(this.audioContext.currentTime);
                oscillator.stop(this.audioContext.currentTime + 0.3);
                
                return;
            }
            
            // Play the physically-synthesized sound
            const source = this.audioContext.createBufferSource();
            const gain = this.audioContext.createGain();
            
            source.buffer = buffer;
            source.connect(gain);
            gain.connect(this.creatureGainNode);
            
            // Volume control
            gain.gain.value = 0.05;
            
            source.start(this.audioContext.currentTime);
            source.stop(this.audioContext.currentTime + 0.3);
            
            console.log(`[Creature Voice] PHYSICAL discovery sound for ${element.type}: ${element.phoneticValue}`);
        } catch (error) {
            console.warn('[Creature Voice] Failed to create discovery sound:', error);
        }
    }

    expressGrowthSound(): void {
        try {
            if (!this.audioContext || !this.creatureGainNode || this.audioContext.state !== 'running') {
                console.log('[Creature Voice] Audio context not available for growth sound');
                return;
            }
            
            // Use PHYSICAL MODELING for growth sound
            const energy = this.voiceState.energyLevel;
            let buffer: AudioBuffer;
            
            if (this.physicalSynthesizer) {
                console.log('[Creature Voice] Using PHYSICAL MODELING for growth sound');
                // Use modal synthesis with rising excitement
                buffer = this.physicalSynthesizer.synthesizeDiscoverySound(200, 0.5, energy);
            } else {
                // Fallback to simple oscillator
                console.warn('[Creature Voice] Physical synthesizer not available, using fallback');
                const oscillator = this.audioContext.createOscillator();
                const gain = this.audioContext.createGain();
                
                oscillator.connect(gain);
                gain.connect(this.creatureGainNode);
                
                // Growth sound - rising tone
                oscillator.frequency.setValueAtTime(200, this.audioContext.currentTime);
                oscillator.frequency.linearRampToValueAtTime(400, this.audioContext.currentTime + 0.5);
                oscillator.type = 'sine';
                
                gain.gain.setValueAtTime(0, this.audioContext.currentTime);
                gain.gain.linearRampToValueAtTime(0.001, this.audioContext.currentTime + 0.1); // Reduced to prevent clipping
                gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);
                
                oscillator.start(this.audioContext.currentTime);
                oscillator.stop(this.audioContext.currentTime + 0.5);
                
                console.log('[Creature Voice] Growth sound (fallback)');
                return;
            }
            
            // Play the physically-synthesized sound
            const source = this.audioContext.createBufferSource();
            const gain = this.audioContext.createGain();
            
            source.buffer = buffer;
            source.connect(gain);
            gain.connect(this.creatureGainNode);
            
            gain.gain.value = 0.035;
            
            source.start(this.audioContext.currentTime);
            source.stop(this.audioContext.currentTime + 0.5);
            
            console.log('[Creature Voice] PHYSICAL growth sound');
        } catch (error) {
            console.warn('[Creature Voice] Failed to create growth sound:', error);
        }
    }

    vocalizeConnectionSound(nodeId: number): void {
        try {
            if (!this.audioContext || !this.creatureGainNode || this.audioContext.state !== 'running') {
                console.log('[Creature Voice] Audio context not available for connection sound');
                return;
            }

            // Prefer PHYSICAL MODELING if available
            if (this.physicalSynthesizer) {
                // Estimate spike rate from node energy/activity
                const node = this.network.nodes[nodeId];
                const energy = node && typeof node.energy === 'number' ? node.energy : 0.5;
                const spikeRateHz = Math.max(5, Math.min(120, 10 + energy * 100));
                const duration = 0.2;
                const intensity = Math.max(0.1, Math.min(1.0, energy));

                const buffer = this.physicalSynthesizer.synthesizeNeuralImpulseConnection(spikeRateHz, duration, intensity);

                const source = this.audioContext.createBufferSource();
                const gain = this.audioContext.createGain();
                source.buffer = buffer;
                source.connect(gain);
                gain.connect(this.creatureGainNode);

                gain.gain.setValueAtTime(0.0, this.audioContext.currentTime);
                gain.gain.linearRampToValueAtTime(0.025, this.audioContext.currentTime + 0.01);
                gain.gain.setValueAtTime(0.025, this.audioContext.currentTime + duration - 0.03);
                gain.gain.linearRampToValueAtTime(0.0001, this.audioContext.currentTime + duration);

                source.start(this.audioContext.currentTime);
                source.stop(this.audioContext.currentTime + duration + 0.001);

                console.log(`[Creature Voice] PHYSICAL connection sound for node ${nodeId}`);
                return;
            }

            // Fallback to simple oscillator if physical synthesizer is not ready
            const oscillator = this.audioContext.createOscillator();
            const gain = this.audioContext.createGain();
            oscillator.connect(gain);
            gain.connect(this.creatureGainNode);
            const baseFreq = 220 + (nodeId % 10) * 50;
            oscillator.frequency.setValueAtTime(baseFreq, this.audioContext.currentTime);
            oscillator.type = 'sine';
            gain.gain.setValueAtTime(0, this.audioContext.currentTime);
            gain.gain.linearRampToValueAtTime(0.001, this.audioContext.currentTime + 0.05);
            gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2);
            oscillator.start(this.audioContext.currentTime);
            oscillator.stop(this.audioContext.currentTime + 0.2);
            console.log(`[Creature Voice] Connection sound (fallback) for node ${nodeId}`);
        } catch (error) {
            console.warn('[Creature Voice] Failed to create connection sound:', error);
        }
    }

    // Helper methods for creature voice
    getCurrentVoiceState(): CreatureVoiceState {
        return { ...this.voiceState };
    }

    updateVoiceFromNetworkActivity(): void {
        // Update voice state based on current network activity
        const avgEnergy = this.network.nodes.reduce((sum, node) => sum + (node.energy || 0), 0) / this.network.nodes.length;
        const connectionCount = this.network.edges.length;
        const linguisticComplexity = this.network.linguisticComplexity;
        
        this.voiceState.energyLevel = avgEnergy;
        this.voiceState.connectionHarmony = Math.min(1, connectionCount / 100);
        this.voiceState.discoveryExcitement = Math.min(1, linguisticComplexity / 5);
        
        // Update growth phase based on network maturity
        if (this.network.nodes.length < 20) this.voiceState.growthPhase = 'infant';
        else if (this.network.nodes.length < 50) this.voiceState.growthPhase = 'child';
        else if (this.network.nodes.length < 100) this.voiceState.growthPhase = 'adolescent';
        else if (this.network.nodes.length < 200) this.voiceState.growthPhase = 'adult';
        else this.voiceState.growthPhase = 'elder';
        
        // Try to initialize audio if network audio context is now available
        if (!this.audioContext && this.network.audioContext && this.network.audioContext.state === 'running') {
            this.initializeCreatureAudio();
        }
    }

    silenceCreature(): void {
        if (this.currentUtterance) {
            window.speechSynthesis.cancel();
            this.currentUtterance = null;
        }
        console.log('[Creature Voice] Creature silenced');
    }

    // --- Text-to-Speech (physical) ---
    // Converts text to simple syllables and renders with the physical synthesizer
    async speakText(text: string, speed: number = 1.0): Promise<void> {
        try {
            if (!this.audioContext || !this.creatureGainNode || this.audioContext.state !== 'running') {
                console.log('[Creature Voice] Audio context not ready for speakText');
                return;
            }
            if (!this.physicalSynthesizer) {
                // Try initialize if missing
                this.initializeCreatureAudio();
                if (!this.physicalSynthesizer) return;
            }

            const syllables = this.textToSyllables(text);
            if (syllables.length === 0) return;

            const basePitch = Math.max(90, Math.min(280, this.voiceCharacteristics.pitch));
            const baseVelocity = 0.6;

            // Duration per syllable influenced by speed
            const sylDuration = Math.max(0.08, 0.18 / Math.max(0.25, speed));

            // Render each syllable to buffer then concatenate
            const sampleRate = this.audioContext.sampleRate;
            const rendered: AudioBuffer[] = [];
            for (const syl of syllables) {
                const pitch = basePitch * (1 + (Math.random() - 0.5) * 0.05);
                const duration = sylDuration * (1 + (Math.random() - 0.5) * 0.2);
                const velocity = baseVelocity * (1 + (Math.random() - 0.5) * 0.2);

                const buffer = this.physicalSynthesizer.synthesizeSyllable(
                    syl,
                    pitch,
                    duration,
                    Math.max(0.05, Math.min(1.0, velocity))
                );
                rendered.push(buffer);
            }

            const totalLength = rendered.reduce((sum, b) => sum + b.length, 0);
            const out = this.audioContext.createBuffer(1, totalLength, sampleRate);
            const outData = out.getChannelData(0);
            let offset = 0;
            for (const buf of rendered) {
                outData.set(buf.getChannelData(0), offset);
                offset += buf.length;
            }

            const source = this.audioContext.createBufferSource();
            source.buffer = out;
            const gain = this.audioContext.createGain();
            source.connect(gain);
            gain.connect(this.creatureGainNode);

            // Gentle envelope for the utterance
            const now = this.audioContext.currentTime + 0.02;
            gain.gain.setValueAtTime(0.0001, now);
            gain.gain.linearRampToValueAtTime(0.045, now + 0.03);
            const end = now + out.duration;
            gain.gain.setValueAtTime(0.045, Math.max(now, end - 0.04));
            gain.gain.linearRampToValueAtTime(0.0001, end);

            source.start(now);
            source.stop(end + 0.005);
        } catch (e) {
            console.warn('[Creature Voice] speakText failed:', e);
        }
    }

    private textToSyllables(text: string): Array<{ consonant?: string; vowel: string }> {
        // Lowercase, keep a-z and spaces
        const cleaned = (text || '')
            .toLowerCase()
            .replace(/[^a-z\s]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        if (!cleaned) return [];

        const words = cleaned.split(' ');
        const syllables: Array<{ consonant?: string; vowel: string }> = [];

        // Simple English-like mapping; prioritizes vowels we have in the synthesizer
        const vowelMap: Array<{ re: RegExp; v: string }> = [
            { re: /ou/, v: 'ou' },
            { re: /ae/, v: 'ae' },
            { re: /oo/, v: 'u' },
            { re: /ee/, v: 'i' },
            { re: /ea/, v: 'e' },
            { re: /ai/, v: 'e' },
            { re: /ia/, v: 'a' },
        ];
        const singleVowels = new Set(['a','e','i','o','u']);

        const toConsonant = (ch: string): string | undefined => {
            // Allow only consonants supported by ConsonantSynthesizer map
            const supported = new Set(['p','b','t','d','k','g','f','v','s','z','ʃ','ʒ','m','n','ŋ']);
            if (supported.has(ch)) return ch;
            // Basic mapping for common English letters to supported set
            switch (ch) {
                case 'h': return 'f';
                case 'c': return 'k';
                case 'q': return 'k';
                case 'x': return 's';
                case 'j': return 'ʒ';
                case 'r': return 'ɹ'; // not synthesized as consonant; drop
                case 'l': return 'l'; // not mapped in consonant synthesizer; drop
                case 'y': return 'j'; // glide; not used by consonant models
                default: return undefined;
            }
        };

        for (const w of words) {
            let i = 0;
            while (i < w.length) {
                // Try digraph vowels first
                let matched = false;
                for (const m of vowelMap) {
                    const sub = w.slice(i);
                    const mt = sub.match(m.re);
                    if (mt && mt.index === 0) {
                        // Optional preceding consonant
                        const cPrev = i > 0 ? toConsonant(w[i - 1]) : undefined;
                        syllables.push({ consonant: cPrev, vowel: m.v });
                        i += mt[0].length;
                        matched = true;
                        break;
                    }
                }
                if (matched) continue;

                const ch = w[i];
                if (singleVowels.has(ch)) {
                    const cPrev = i > 0 ? toConsonant(w[i - 1]) : undefined;
                    syllables.push({ consonant: cPrev, vowel: ch });
                    i += 1;
                    continue;
                }
                i += 1;
            }
        }

        // Fallback: if nothing detected, hum a neutral vowel
        if (syllables.length === 0) syllables.push({ vowel: 'a' });
        return syllables;
    }

    private calculateEvolvedVoice(networkState: any, evolutionFactor: number): VoiceCharacteristics {
        const baseVoice = this.voiceCharacteristics;
        
        // Ensure evolution factor is finite
        const safeEvolutionFactor = isFinite(evolutionFactor) ? evolutionFactor : 0;
        
        return {
            ...baseVoice,
            pitch: Math.max(50, Math.min(500, baseVoice.pitch * (1 + safeEvolutionFactor * 0.1))),
            pitchVariation: Math.max(5, Math.min(50, baseVoice.pitchVariation * (1 + safeEvolutionFactor * 0.2))),
            formant1: Math.max(200, Math.min(2000, baseVoice.formant1 * (1 + safeEvolutionFactor * 0.05))),
            formant2: Math.max(500, Math.min(3000, baseVoice.formant2 * (1 + safeEvolutionFactor * 0.05))),
            formant3: Math.max(1000, Math.min(5000, baseVoice.formant3 * (1 + safeEvolutionFactor * 0.05))),
            breathiness: Math.max(0.05, Math.min(0.8, baseVoice.breathiness - safeEvolutionFactor * 0.1)),
            roughness: Math.max(0.02, Math.min(0.5, baseVoice.roughness - safeEvolutionFactor * 0.05)),
            speechRate: Math.max(0.5, Math.min(2.0, baseVoice.speechRate * (1 + safeEvolutionFactor * 0.1))),
            emphasisStrength: Math.max(0.1, Math.min(2.0, baseVoice.emphasisStrength * (1 + safeEvolutionFactor * 0.15))),
            personality: this.determineCreaturePersonality(),
            age: this.determineCreatureAge(networkState.nodeCount / 100)
        };
    }

    private applyCreatureVoiceCharacteristics(utterance: SpeechSynthesisUtterance): void {
        const voices = window.speechSynthesis.getVoices();
        const selectedVoice = this.selectVoiceForCreature(voices);
        
        if (selectedVoice) {
            utterance.voice = selectedVoice;
        }
        
        utterance.rate = this.voiceCharacteristics.speechRate * 0.8; // Slower speech for clarity
        utterance.pitch = this.calculateCreaturePitch();
        utterance.volume = 1.0; // Maximum value for SpeechSynthesisUtterance
        
        // Try to boost volume through additional properties
        (utterance as any).volume = 1.0;
        (utterance as any).speakVolume = 1.0;
        
        // Try setting additional volume properties that might work
        try {
            (utterance as any).speakVolume = 1.0;
            (utterance as any).speakVolumeLevel = 1.0;
        } catch (e) {
            // Ignore if properties don't exist
        }
        
        console.log(`[Creature Voice] Voice settings - Volume: ${utterance.volume}, Rate: ${utterance.rate}, Pitch: ${utterance.pitch}`);
    }

    private selectVoiceForCreature(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
        // Select voice based on creature's current characteristics
        if (this.voiceCharacteristics.gender === 'feminine') {
            return voices.find(v => v.name.toLowerCase().includes('female') || 
                                   v.name.toLowerCase().includes('woman') || 
                                   v.name.toLowerCase().includes('samantha')) || voices[0];
        } else if (this.voiceCharacteristics.gender === 'masculine') {
            return voices.find(v => v.name.toLowerCase().includes('male') || 
                                   v.name.toLowerCase().includes('man') || 
                                   v.name.toLowerCase().includes('alex')) || voices[0];
        }
        
        return voices.find(v => v.default) || voices[0];
    }

    private calculateCreaturePitch(): number {
        // Convert creature's pitch to SpeechSynthesis range
        const pitch = this.voiceCharacteristics.pitch;
        
        // Ensure pitch is finite and valid
        if (!isFinite(pitch) || isNaN(pitch)) {
            console.warn('[Creature Voice] Invalid pitch value detected, using default');
            return 1.0; // Default pitch
        }
        
        const normalizedPitch = (pitch - 100) / 200;
        const clampedPitch = Math.max(0.5, Math.min(2.0, normalizedPitch));
        
        // Final safety check
        return isFinite(clampedPitch) ? clampedPitch : 1.0;
    }

    private generateDiscoveryThought(element: LinguisticElement, excitement: number): string {
        // Dynamic discovery speech based on network growth and learned linguistic structures
        const learnedElements = Array.from(this.network.linguisticElements.values());
        
        if (learnedElements.length === 0) {
            // Infant stage: pure phonemes with natural articulation
            return this.pronounceWithArticulation(element);
        }
        
        // Analyze the discovered element's phonetic properties
        const elementCategory = this.categorizeElement(element);
        const masteredElements = learnedElements.filter(e => e.learningPhase === 'mastery' || e.learningPhase === 'teaching');
        const practicingElements = learnedElements.filter(e => e.learningPhase === 'practice');
        
        // Generate speech based on creature's linguistic development stage
        const networkComplexity = this.network.nodes.length;
        
        if (networkComplexity < 30) {
            // Early discovery: simple reduplicated babbling with new element
            return this.generateBabblingDiscovery(element, excitement);
        } else if (networkComplexity < 100) {
            // Developing: use phonetic similarity to create meaningful-sounding utterances
            return this.generatePhoneticExclamation(element, masteredElements, excitement);
        } else if (networkComplexity < 300) {
            // Advanced: use prosodic patterns and harmonic progressions
            return this.generateProsodicDiscovery(element, masteredElements, practicingElements, excitement);
        } else {
            // Mature: complex musical utterances with learned phonetic structures
            return this.generateMusicalDiscovery(element, learnedElements, excitement);
        }
    }

    private generateEmotionalThought(emotion: string, intensity: number): string {
        // Dynamic emotional expression based on learned linguistic patterns and network state
        const learnedElements = Array.from(this.network.linguisticElements.values());
        
        if (learnedElements.length === 0) {
            // Infant stage: basic emotional vocalizations
            return this.generateInfantEmotionalSound(emotion, intensity);
        }
        
        // Filter elements by learning phase - prefer mastered elements for emotional expression
        const masteredElements = learnedElements.filter(e => 
            e.learningPhase === 'mastery' || e.learningPhase === 'teaching'
        );
        const availableElements = masteredElements.length > 5 ? masteredElements : learnedElements;
        
        // Select elements based on phonetic properties that match the emotion
        const emotionElements = this.selectElementsByEmotion(availableElements, emotion);
        
        // Generate speech based on creature's development and emotional intensity
        const networkComplexity = this.network.nodes.length;
        
        if (networkComplexity < 30) {
            // Simple emotional vocalization with repetition
            return this.generateEmotionalVocalization(emotionElements, emotion, intensity);
        } else if (networkComplexity < 100) {
            // Prosodic emotional expression with intonation patterns
            return this.generateProsodicEmotion(emotionElements, emotion, intensity);
        } else if (networkComplexity < 300) {
            // Complex melodic emotional patterns
            return this.generateMelodicEmotion(emotionElements, emotion, intensity);
        } else {
            // Sophisticated harmonic emotional expression
            return this.generateHarmonicEmotion(learnedElements, emotion, intensity);
        }
    }
    
    // === DYNAMIC LEARNING-BASED SPEECH GENERATION METHODS ===
    
    /**
     * Categorizes a linguistic element by its phonetic properties
     */
    private categorizeElement(element: LinguisticElement): string {
        if (element.type === 'vowel') {
            return `${element.vowelHeight || 'mid'}_${element.vowelBackness || 'central'}_vowel`;
        } else if (element.type === 'consonant') {
            return `${element.placeOfArticulation || 'alveolar'}_${element.mannerOfArticulation || 'plosive'}`;
        } else {
            return element.type;
        }
    }
    
    /**
     * Pronounces an element with natural articulation features
     */
    private pronounceWithArticulation(element: LinguisticElement): string {
        const base = this.convertToPronounceableSound(element.phoneticValue);
        
        // Add articulation characteristics
        if (element.type === 'vowel') {
            // Elongate according to vowel properties
            if (element.vowelHeight === 'open' || element.vowelHeight === 'open_mid') {
                return base + base.charAt(base.length - 1); // Lengthen open vowels
            }
        } else if (element.type === 'consonant') {
            // Add aspiration or other features
            if (element.voicing === 'voiceless' && element.mannerOfArticulation === 'plosive') {
                return base + 'h'; // Aspirated plosives
            }
        }
        
        return base;
    }
    
    /**
     * Infant-stage babbling with reduplicated syllables
     */
    private generateBabblingDiscovery(element: LinguisticElement, excitement: number): string {
        const base = this.pronounceWithArticulation(element);
        const repetitions = excitement > 0.7 ? 3 : excitement > 0.4 ? 2 : 1;
        
        let babble = '';
        for (let i = 0; i < repetitions; i++) {
            babble += base;
            if (i < repetitions - 1) babble += '-';
        }
        
        return excitement > 0.8 ? babble + '!' : babble;
    }
    
    /**
     * Generate phonetically similar exclamation using mastered elements
     */
    private generatePhoneticExclamation(
        element: LinguisticElement, 
        masteredElements: LinguisticElement[], 
        excitement: number
    ): string {
        if (masteredElements.length === 0) {
            return this.generateBabblingDiscovery(element, excitement);
        }
        
        // Find phonetically similar elements (same place or manner)
        const similar = masteredElements.filter(e => {
            if (element.type === 'vowel' && e.type === 'vowel') {
                return e.vowelHeight === element.vowelHeight || e.vowelBackness === element.vowelBackness;
            } else if (element.type === 'consonant' && e.type === 'consonant') {
                return e.placeOfArticulation === element.placeOfArticulation || 
                       e.mannerOfArticulation === element.mannerOfArticulation;
            }
            return false;
        });
        
        const elementsToUse = similar.length > 0 ? similar : masteredElements;
        const selected = elementsToUse.slice(0, 2 + Math.floor(excitement * 2));
        
        // Build CV or CVC pattern
        const consonants = selected.filter(e => e.type === 'consonant');
        const vowels = selected.filter(e => e.type === 'vowel');
        
        let utterance = '';
        if (consonants.length > 0 && vowels.length > 0) {
            // CVC pattern with new element
            utterance += this.convertToPronounceableSound(consonants[0].phoneticValue);
            utterance += this.pronounceWithArticulation(element);
            if (consonants.length > 1) {
                utterance += this.convertToPronounceableSound(consonants[1].phoneticValue);
            }
        } else {
            // Just the new element with mastered context
            if (selected.length > 0) {
                utterance += this.convertToPronounceableSound(selected[0].phoneticValue);
            }
            utterance += this.pronounceWithArticulation(element);
        }
        
        return excitement > 0.7 ? utterance + '!' : utterance;
    }
    
    /**
     * Prosodic discovery with stress and intonation patterns
     */
    private generateProsodicDiscovery(
        element: LinguisticElement,
        masteredElements: LinguisticElement[],
        practicingElements: LinguisticElement[],
        excitement: number
    ): string {
        // Combine mastered and practicing elements
        const allElements = [...masteredElements, ...practicingElements.slice(0, 3)];
        
        if (allElements.length === 0) {
            return this.generateBabblingDiscovery(element, excitement);
        }
        
        // Create a prosodic phrase with stress pattern
        const phraseLength = 2 + Math.floor(excitement * 2); // 2-4 elements
        const phrase: string[] = [];
        
        // Select diverse elements for the phrase
        const vowels = allElements.filter(e => e.type === 'vowel');
        const consonants = allElements.filter(e => e.type === 'consonant');
        
        for (let i = 0; i < phraseLength; i++) {
            if (i % 2 === 0 && consonants.length > 0) {
                // Consonant position
                const cons = consonants[i % consonants.length];
                phrase.push(this.convertToPronounceableSound(cons.phoneticValue));
            } else if (vowels.length > 0) {
                // Vowel position
                const vow = vowels[i % vowels.length];
                phrase.push(this.convertToPronounceableSound(vow.phoneticValue));
            }
        }
        
        // Insert the new element at a stressed position (beginning or middle)
        const stressPosition = excitement > 0.6 ? 0 : Math.floor(phrase.length / 2);
        phrase[stressPosition] = this.pronounceWithArticulation(element).toUpperCase();
        
        const utterance = phrase.join('');
        return excitement > 0.7 ? utterance + '!' : utterance;
    }
    
    /**
     * Musical discovery with harmonic and melodic patterns
     */
    private generateMusicalDiscovery(
        element: LinguisticElement,
        learnedElements: LinguisticElement[],
        excitement: number
    ): string {
        // Use musical properties if available
        const musicalElements = learnedElements.filter(e => e.musicalPitch || e.melodicContext);
        
        if (musicalElements.length === 0) {
            return this.generateProsodicDiscovery(element, learnedElements, [], excitement);
        }
        
        // Create ascending melodic pattern for discovery (rising excitement)
        const ascendingElements = musicalElements
            .sort((a, b) => (a.musicalPitch || 0) - (b.musicalPitch || 0))
            .slice(0, 4);
        
        let melody = '';
        ascendingElements.forEach((el, i) => {
            melody += this.convertToPronounceableSound(el.phoneticValue);
            if (i < ascendingElements.length - 1) melody += '-';
        });
        
        // Add the new element at the peak
        melody += '-' + this.pronounceWithArticulation(element).toUpperCase();
        
        return excitement > 0.8 ? melody + '!!' : melody + '!';
    }
    
    /**
     * Infant emotional vocalizations (basic crying, cooing, etc.)
     */
    private generateInfantEmotionalSound(emotion: string, intensity: number): string {
        const emotionSounds: { [key: string]: string[] } = {
            'happy': ['aaa', 'eee', 'ooo'],
            'excited': ['iii', 'aaa', 'eee'],
            'sad': ['uuu', 'ooo', 'ahh'],
            'angry': ['aah', 'grr', 'mmm'],
            'curious': ['oh', 'ah', 'oo'],
            'contemplative': ['mmm', 'hmm', 'uhh'],
            'content': ['ahh', 'mmm', 'ooo']
        };
        
        const sounds = emotionSounds[emotion] || emotionSounds['curious'];
        const selected = sounds[Math.floor(Math.random() * sounds.length)];
        const repetitions = Math.floor(1 + intensity * 2);
        
        return selected.repeat(repetitions);
    }
    
    /**
     * Select elements that match emotional characteristics
     */
    private selectElementsByEmotion(elements: LinguisticElement[], emotion: string): LinguisticElement[] {
        let selected: LinguisticElement[] = [];
        
        if (emotion === 'happy' || emotion === 'excited') {
            // High, front vowels; voiced consonants
            selected = elements.filter(e => 
                (e.type === 'vowel' && (e.vowelHeight === 'close' || e.vowelHeight === 'close_mid')) ||
                (e.type === 'consonant' && e.voicing === 'voiced')
            );
        } else if (emotion === 'sad' || emotion === 'contemplative') {
            // Low, back vowels; nasal consonants
            selected = elements.filter(e =>
                (e.type === 'vowel' && (e.vowelHeight === 'open' || e.vowelBackness === 'back')) ||
                (e.type === 'consonant' && e.mannerOfArticulation === 'nasal')
            );
        } else if (emotion === 'angry') {
            // Mid vowels; plosive consonants
            selected = elements.filter(e =>
                (e.type === 'vowel' && e.vowelHeight === 'mid') ||
                (e.type === 'consonant' && e.mannerOfArticulation === 'plosive')
            );
        } else if (emotion === 'curious') {
            // Rising intonation - front vowels, approximants
            selected = elements.filter(e =>
                (e.type === 'vowel' && e.vowelBackness === 'front') ||
                (e.type === 'consonant' && e.mannerOfArticulation === 'approximant')
            );
        }
        
        // If we didn't find enough matching elements, use all elements
        return selected.length >= 3 ? selected : elements;
    }
    
    /**
     * Simple emotional vocalization with repetition
     */
    private generateEmotionalVocalization(
        elements: LinguisticElement[], 
        emotion: string, 
        intensity: number
    ): string {
        if (elements.length === 0) {
            return this.generateInfantEmotionalSound(emotion, intensity);
        }
        
        // Select 1-3 elements and repeat based on intensity
        const numElements = Math.min(3, Math.max(1, Math.floor(intensity * 3)));
        const selected = elements.slice(0, numElements);
        
        let vocalization = '';
        selected.forEach((el, i) => {
            vocalization += this.convertToPronounceableSound(el.phoneticValue);
            if (i < selected.length - 1 && intensity > 0.5) {
                vocalization += '-';
            }
        });
        
        // Repeat for intensity
        const repetitions = Math.floor(1 + intensity * 2);
        if (repetitions > 1) {
            vocalization = Array(repetitions).fill(vocalization).join(' ');
        }
        
        return intensity > 0.7 ? vocalization + '!' : vocalization;
    }
    
    /**
     * Prosodic emotional expression with intonation
     */
    private generateProsodicEmotion(
        elements: LinguisticElement[],
        emotion: string,
        intensity: number
    ): string {
        if (elements.length === 0) {
            return this.generateInfantEmotionalSound(emotion, intensity);
        }
        
        // Build prosodic contour based on emotion
        const phraseLength = 2 + Math.floor(intensity * 3);
        const phrase: string[] = [];
        
        const vowels = elements.filter(e => e.type === 'vowel');
        const consonants = elements.filter(e => e.type === 'consonant');
        
        // Create CV syllable pattern
        for (let i = 0; i < phraseLength; i++) {
            let syllable = '';
            if (consonants.length > 0 && i % 2 === 0) {
                syllable += this.convertToPronounceableSound(consonants[i % consonants.length].phoneticValue);
            }
            if (vowels.length > 0) {
                syllable += this.convertToPronounceableSound(vowels[i % vowels.length].phoneticValue);
            }
            phrase.push(syllable);
        }
        
        // Apply prosodic modification based on emotion
        if (emotion === 'happy' || emotion === 'excited') {
            // Rising contour - emphasize end
            phrase[phrase.length - 1] = phrase[phrase.length - 1].toUpperCase();
        } else if (emotion === 'sad') {
            // Falling contour - emphasize beginning
            phrase[0] = phrase[0].toUpperCase();
        } else if (emotion === 'angry') {
            // Flat high contour - emphasize all
            phrase.forEach((p, i) => phrase[i] = p.toUpperCase());
        }
        
        const utterance = phrase.join(' ');
        return intensity > 0.7 ? utterance + '!' : utterance;
    }
    
    /**
     * Melodic emotional patterns using musical properties
     */
    private generateMelodicEmotion(
        elements: LinguisticElement[],
        emotion: string,
        intensity: number
    ): string {
        if (elements.length === 0) {
            return this.generateProsodicEmotion(elements, emotion, intensity);
        }
        
        // Sort by musical pitch if available
        const musicalElements = elements.filter(e => e.musicalPitch).length > 0
            ? elements.filter(e => e.musicalPitch).sort((a, b) => (a.musicalPitch || 0) - (b.musicalPitch || 0))
            : elements;
        
        const phraseLength = 3 + Math.floor(intensity * 2);
        const phrase: string[] = [];
        
        // Create melodic contour based on emotion
        if (emotion === 'happy' || emotion === 'excited') {
            // Ascending melody
            for (let i = 0; i < phraseLength && i < musicalElements.length; i++) {
                phrase.push(this.convertToPronounceableSound(musicalElements[i].phoneticValue));
            }
        } else if (emotion === 'sad' || emotion === 'contemplative') {
            // Descending melody
            for (let i = musicalElements.length - 1; i >= 0 && phrase.length < phraseLength; i--) {
                phrase.push(this.convertToPronounceableSound(musicalElements[i].phoneticValue));
            }
        } else {
            // Arch-shaped melody
            const mid = Math.floor(musicalElements.length / 2);
            for (let i = 0; i < phraseLength && i < musicalElements.length; i++) {
                const idx = i < phraseLength / 2 ? mid + i : mid - (i - Math.floor(phraseLength / 2));
                phrase.push(this.convertToPronounceableSound(musicalElements[Math.max(0, Math.min(musicalElements.length - 1, idx))].phoneticValue));
            }
        }
        
        return phrase.join('-') + (intensity > 0.7 ? '!' : '');
    }
    
    /**
     * Harmonic emotional expression with complex patterns
     */
    private generateHarmonicEmotion(
        elements: LinguisticElement[],
        emotion: string,
        intensity: number
    ): string {
        if (elements.length < 5) {
            return this.generateMelodicEmotion(elements, emotion, intensity);
        }
        
        // Use harmonic function if available
        const harmonicElements = elements.filter(e => e.harmonicFunction);
        const baseElements = harmonicElements.length > 0 ? harmonicElements : elements;
        
        // Group by harmonic function
        const tonic = baseElements.filter(e => e.harmonicFunction === 'tonic');
        const dominant = baseElements.filter(e => e.harmonicFunction === 'dominant');
        const subdominant = baseElements.filter(e => e.harmonicFunction === 'subdominant');
        
        const phrase: string[] = [];
        
        // Build harmonic progression based on emotion
        if (emotion === 'happy' || emotion === 'excited') {
            // I - V - I progression
            if (tonic.length > 0) phrase.push(this.convertToPronounceableSound(tonic[0].phoneticValue));
            if (dominant.length > 0) phrase.push(this.convertToPronounceableSound(dominant[0].phoneticValue));
            if (tonic.length > 0) phrase.push(this.convertToPronounceableSound(tonic[0].phoneticValue));
        } else if (emotion === 'sad') {
            // i - iv - i progression (minor feel)
            if (tonic.length > 0) phrase.push(this.convertToPronounceableSound(tonic[0].phoneticValue));
            if (subdominant.length > 0) phrase.push(this.convertToPronounceableSound(subdominant[0].phoneticValue));
            if (tonic.length > 0) phrase.push(this.convertToPronounceableSound(tonic[0].phoneticValue));
        } else {
            // Generic progression with all functions
            if (tonic.length > 0) phrase.push(this.convertToPronounceableSound(tonic[0].phoneticValue));
            if (subdominant.length > 0) phrase.push(this.convertToPronounceableSound(subdominant[0].phoneticValue));
            if (dominant.length > 0) phrase.push(this.convertToPronounceableSound(dominant[0].phoneticValue));
            if (tonic.length > 0) phrase.push(this.convertToPronounceableSound(tonic[0].phoneticValue));
        }
        
        // If we couldn't build a harmonic progression, fall back to melodic
        if (phrase.length === 0) {
            return this.generateMelodicEmotion(elements, emotion, intensity);
        }
        
        // Add repetitions based on intensity
        if (intensity > 0.7) {
            const repeated = phrase.join('-');
            return repeated + ' ' + repeated + '!';
        }
        
        return phrase.join('-');
    }
    
    // === END OF DYNAMIC LEARNING-BASED SPEECH GENERATION ===

    private createPhoneticCombinations(elements: LinguisticElement[], minLength: number, maxLength: number): string[] {
        const combinations: string[] = [];
        
        // Get vowels and consonants separately for better combinations
        const vowels = elements.filter(e => e.type === 'vowel');
        const consonants = elements.filter(e => e.type === 'consonant');
        
        // Create simple vowel-consonant patterns
        for (let i = 0; i < Math.min(5, vowels.length); i++) {
            for (let j = 0; j < Math.min(3, consonants.length); j++) {
                const vowel = vowels[i];
                const consonant = consonants[j];
                
                // Create different patterns using pronounceable sounds
                combinations.push(this.convertToPronounceableSound(vowel.phoneticValue) + this.convertToPronounceableSound(consonant.phoneticValue));
                combinations.push(this.convertToPronounceableSound(consonant.phoneticValue) + this.convertToPronounceableSound(vowel.phoneticValue));
                
                if (combinations.length >= 10) break; // Limit combinations
            }
            if (combinations.length >= 10) break;
        }
        
        // Add single elements if we don't have enough combinations
        if (combinations.length < 5) {
            elements.slice(0, 5).forEach(element => {
                combinations.push(this.convertToPronounceableSound(element.phoneticValue));
            });
        }
        
        return combinations;
    }
    
    private createEmotionalCombinations(elements: LinguisticElement[], emotion: string, intensity: number): string[] {
        const combinations: string[] = [];
        
        // Select elements based on emotion characteristics
        let selectedElements = elements;
        
        if (emotion === 'happy' || emotion === 'excited') {
            // Use higher frequency vowels for positive emotions
            selectedElements = elements.filter(e => 
                e.type === 'vowel' && (e.frequency && e.frequency > 200)
            ).concat(elements.filter(e => e.type !== 'vowel'));
        } else if (emotion === 'sad' || emotion === 'contemplative') {
            // Use lower frequency vowels for negative emotions
            selectedElements = elements.filter(e => 
                e.type === 'vowel' && (e.frequency && e.frequency < 250)
            ).concat(elements.filter(e => e.type !== 'vowel'));
        }
        
        // Create combinations based on intensity
        const combinationLength = intensity > 0.7 ? 3 : 2;
        const numCombinations = intensity > 0.7 ? 5 : 3;
        
        for (let i = 0; i < numCombinations && i < selectedElements.length; i++) {
            const element = selectedElements[i];
            let combination = this.convertToPronounceableSound(element.phoneticValue);
            
            // Add more elements for higher intensity
            if (combinationLength > 1) {
                const additionalElements = selectedElements
                    .filter((_, idx) => idx !== i)
                    .slice(0, combinationLength - 1);
                
                additionalElements.forEach(additional => {
                    combination += this.convertToPronounceableSound(additional.phoneticValue);
                });
            }
            
            combinations.push(combination);
        }
        
        return combinations;
    }

    private convertToPronounceableSound(phoneticValue: string): string {
        // Convert phonetic symbols to pronounceable sounds
        const phoneticMap: { [key: string]: string } = {
            // Basic vowels
            'a': 'ah',
            'e': 'eh', 
            'i': 'ee',
            'o': 'oh',
            'u': 'oo',
            
            // Extended vowels
            'æ': 'a', // as in "cat"
            'ɑ': 'ah', // as in "father"
            'ɛ': 'e', // as in "bed"
            'ɪ': 'i', // as in "bit"
            'ɔ': 'aw', // as in "bought"
            'ʊ': 'oo', // as in "book"
            'ʌ': 'uh', // as in "but"
            'ə': 'uh', // schwa
            'y': 'ee', // as in "see"
            'ø': 'er', // as in "her"
            'œ': 'er', // as in "her"
            
            // Consonants - plosives
            'p': 'puh',
            'b': 'buh',
            't': 'tuh',
            'd': 'duh',
            'k': 'kuh',
            'g': 'guh',
            'q': 'kuh',
            'ɢ': 'guh',
            'ʔ': 'uh', // glottal stop
            
            // Nasals
            'm': 'mm',
            'n': 'nn',
            'ŋ': 'ng',
            'ɲ': 'ny',
            'ɴ': 'ng',
            
            // Fricatives
            'f': 'ff',
            'v': 'vv',
            'θ': 'th', // as in "think"
            'ð': 'th', // as in "this"
            's': 'ss',
            'z': 'zz',
            'ʃ': 'sh',
            'ʒ': 'zh',
            'x': 'ch', // as in "loch"
            'ɣ': 'gh',
            'χ': 'ch',
            'ʁ': 'r',
            'ħ': 'h',
            'ʕ': 'ah',
            'h': 'h',
            
            // Approximants
            'l': 'll',
            'ɭ': 'll',
            'ʎ': 'ly',
            'j': 'y',
            'w': 'w',
            'ɥ': 'w',
            'ʍ': 'wh',
            
            // Taps and trills
            'ɾ': 'r',
            'r': 'rr',
            'ʀ': 'rr',
            
            // Affricates
            'tʃ': 'ch',
            'dʒ': 'j',
            'ts': 'ts',
            'dz': 'dz',
            'tɕ': 'ch',
            'dʑ': 'j',
            
            // Other symbols - diacritics and modifiers
            'ː': '', // length marker
            'ˈ': '', // primary stress
            'ˌ': '', // secondary stress
            'ˑ': '', // half-long
            '̆': '', // extra-short
            '̥': '', // voiceless
            '̬': '', // voiced
            'ʰ': '', // aspirated
            'ʲ': '', // palatalized
            'ʷ': '', // labialized
            'ˠ': '', // velarized
            'ˤ': '', // pharyngealized
            '̃': '', // nasalized
            '̰': '', // creaky
            '̤': '', // breathy
            '̪': '', // dental
            '̺': '', // apical
            '̻': '', // laminal
            '̼': '', // linguolabial
            '̝': '', // raised
            '̞': '', // lowered
            '̟': '', // advanced
            '̠': '', // retracted
            '̈': '', // centralized
            '̽': '', // mid-centralized
            '̩': '', // syllabic
            '̯': '', // non-syllabic
            '̚': '' // no audible release
        };
        
        // Convert the phonetic value to pronounceable sound
        let result = phoneticValue;
        
        // Replace each phonetic symbol with its pronunciation
        for (const [symbol, pronunciation] of Object.entries(phoneticMap)) {
            result = result.replace(new RegExp(symbol, 'g'), pronunciation);
        }
        
        // Clean up any remaining special characters
        result = result.replace(/[^\w\s]/g, '');
        
        // If result is empty or too short, use a fallback
        if (result.length === 0) {
            result = phoneticValue.length > 1 ? phoneticValue : 'uh';
        }
        
        return result;
    }

    private applyBreathingConstraints(thresholds: VoiceCapacityThresholds): void {
        // Always apply breathing constraints since breath is always required
        if (thresholds.maxIntensity < 1.0) {
            // Reduce voice intensity based on breathing capacity
            this.voiceCharacteristics.pitch *= thresholds.maxIntensity;
            this.voiceCharacteristics.emphasisStrength *= thresholds.maxIntensity;
            this.voiceCharacteristics.speechRate *= (0.5 + thresholds.maxIntensity * 0.5); // Slower speech when breathing is limited
        }
        
        // Adjust breathiness based on oxygen levels
        const breathingState = this.breathingSystem.getBreathingState();
        if (breathingState.oxygenLevel < 0.7) {
            this.voiceCharacteristics.breathiness = Math.min(0.8, this.voiceCharacteristics.breathiness + 0.3);
        }
        
        // Adjust roughness based on CO2 levels
        if (breathingState.carbonDioxideLevel > 0.4) {
            this.voiceCharacteristics.roughness = Math.min(0.6, this.voiceCharacteristics.roughness + 0.2);
        }
        
        // Always apply breathing-required constraints since breath is always required
        this.voiceCharacteristics.pauseFrequency = Math.min(0.8, this.voiceCharacteristics.pauseFrequency + 0.3);
        this.voiceCharacteristics.pauseDuration = Math.max(500, this.voiceCharacteristics.pauseDuration + 200);
        
        // Additional breathing constraints for always-required breathing
        this.voiceCharacteristics.speechRate *= 0.8; // Slower speech to accommodate breathing
        this.voiceCharacteristics.emphasisStrength *= 0.9; // Reduced emphasis due to breathing needs
        
        // Only log constraints occasionally to avoid spam
        // console.log(`[Creature Voice] Applied breathing constraints - intensity: ${thresholds.maxIntensity.toFixed(2)}, breath required: ${thresholds.breathRequired}`);
    }

    private logVoiceAudit(system: string, action: string, details: string, duration?: number): void {
        const entry = {
            timestamp: Date.now(),
            system,
            action,
            details,
            duration,
            timeSinceStart: Date.now() - this.voiceSystemStartTime
        };
        
        this.voiceAuditEntries.push(entry);
        
        // Keep only recent entries
        if (this.voiceAuditEntries.length > 500) {
            this.voiceAuditEntries = this.voiceAuditEntries.slice(-500);
        }
        
        // Only log to console for critical events to avoid spam
        const criticalActions = ['INITIALIZATION', 'ERROR', 'VOICE_UNLOCKED'];
        if (criticalActions.includes(action)) {
            const durationStr = duration ? ` (${duration.toFixed(2)}ms)` : '';
            console.log(`[VOICE_AUDIT ${entry.timeSinceStart}ms] ${system}.${action}: ${details}${durationStr}`);
        }
    }

    getVoiceAuditLog(): any[] {
        return [...this.voiceAuditEntries];
    }

    private isSupported(): boolean {
        return 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
    }

    // Test method to manually trigger creature voice
    testVoice(): void {
        console.log('[Creature Voice] Testing voice system...');
        
        // Use learned elements for testing
        const learnedElements = Array.from(this.network.linguisticElements.values());
        
        if (learnedElements.length > 0) {
            const testCombination = learnedElements
                .slice(0, 3)
                .map(e => this.convertToPronounceableSound(e.phoneticValue))
                .join('');
            this.speakCreatureThought(testCombination);
        } else {
            // Fallback to basic sounds
            this.speakCreatureThought("ah eh ee oh oo");
        }
    }
}

// Factory function to create creature voice system
export function createCreatureVoiceSystem(network: NeuralNetworkSimulator, breathingSystem: BreathingSystem): CreatureVoiceSystem {
    return new CreatureVoiceSystem(network, breathingSystem);
}

